import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ShiftsEditor() {
  const [shifts, setShifts] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shifts").then(({ data }) => setShifts(data));
  }, []);

  if (!shifts) return <div className="text-muted-foreground">Loading…</div>;

  const update = (i, key, v) => setShifts((s) => s.map((x, j) => (j === i ? { ...x, [key]: v } : x)));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/shifts", { shifts });
      setShifts(data);
      toast.success("Shifts saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save shifts");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4" data-testid="shifts-editor">
      <div className="flex items-center gap-3">
        <Clock strokeWidth={1.5} className="w-5 h-5 text-primary" />
        <h2 className="font-heading text-xl font-semibold">Shift timings</h2>
      </div>
      <p className="text-sm text-muted-foreground">Used by the Item-wise report. A shift can cross midnight (e.g. 22:00 → 06:00).</p>
      <div className="space-y-2">
        {shifts.map((s, i) => (
          <div key={i} className="flex items-center gap-2 bg-card border border-border p-3" data-testid={`shift-row-${i}`}>
            <input data-testid={`shift-name-${i}`} value={s.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Shift name"
              className="flex-1 min-w-0 bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            <input data-testid={`shift-start-${i}`} type="time" value={s.start} onChange={(e) => update(i, "start", e.target.value)}
              className="bg-background border border-border px-2 py-2 text-sm" />
            <span className="text-muted-foreground text-sm">to</span>
            <input data-testid={`shift-end-${i}`} type="time" value={s.end} onChange={(e) => update(i, "end", e.target.value)}
              className="bg-background border border-border px-2 py-2 text-sm" />
            <button data-testid={`shift-remove-${i}`} onClick={() => setShifts((x) => x.filter((_, j) => j !== i))} disabled={shifts.length === 1}
              className="p-2 text-muted-foreground hover:text-destructive disabled:opacity-30">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button data-testid="shift-add-btn" onClick={() => setShifts((x) => [...x, { name: "", start: "09:00", end: "17:00" }])}
          className="flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-heading uppercase tracking-widest hover:bg-muted">
          <Plus className="w-4 h-4" /> Add shift
        </button>
        <button data-testid="shift-save-btn" onClick={save} disabled={busy}
          className="flex-1 bg-primary text-primary-foreground py-2.5 font-heading uppercase tracking-widest text-sm hover:opacity-90 active:scale-[0.98] disabled:opacity-40">
          {busy ? "Saving…" : "Save shifts"}
        </button>
      </div>
    </div>
  );
}
